import type { FactoryMatchOption, Step } from "@views/Upholstery/types"

/**
 * Factory-match seat diagram tile subtitles.
 *
 * - **Vehicle model**: selected YMM from Dato (e.g. "Jeep Gladiator").
 * - **Row schematic**: e.g. "60-40 Schematic" when legacy Dato used row config.
 *
 * Mode is inferred from Dato `factoryMatchOption` samples when available;
 * static catalog/blocklist is fallback only.
 */

export type SeatDiagramVehicleModelEntry = {
  make: string
  model?: string
}

export type SeatDiagramDisplayMode = "vehicle-model" | "row-schematic"

export type SeatDiagramLabelContext = {
  selectedMake: string
  selectedModel: string
  displayMode: SeatDiagramDisplayMode
}

/** Dato `allUpholsteryCars` (fallback when Dato factory-match sample is missing). */
export const SEAT_DIAGRAM_VEHICLE_MODEL_CATALOG: SeatDiagramVehicleModelEntry[] =
  [
    { make: "Honda", model: "Civic" },
    { make: "Jeep", model: "Gladiator" },
    { make: "Honda", model: "Accord" },
    { make: "Hyundai", model: "Tucson" },
    { make: "GMC", model: "Yukon XL" },
    { make: "GMC", model: "Acadia" },
    { make: "GMC", model: "Yukon" },
    { make: "GMC", model: "Sierra 3500 HD" },
    { make: "GMC", model: "Sierra 2500 HD" },
    { make: "GMC", model: "Sierra 1500" },
    { make: "Chevrolet", model: "Silverado 1500" },
    { make: "Chevrolet", model: "Tahoe" },
    { make: "Chevrolet", model: "Suburban" },
    { make: "Chevrolet", model: "Trailblazer" },
    { make: "Chevrolet", model: "Traverse" },
    { make: "Chevrolet", model: "Silverado 2500 HD" },
    { make: "Chevrolet", model: "Colorado" },
    { make: "Chevrolet", model: "Silverado 3500 HD" },
    { make: "GMC", model: "Canyon" },
    { make: "Toyota", model: "4Runner" },
    { make: "Ford", model: "F-150" },
    { make: "Toyota", model: "Camry" },
    { make: "Toyota", model: "Tundra" },
    { make: "Toyota", model: "Tacoma" },
    { make: "Toyota", model: "RAV4 LE/XLE" },
    { make: "Toyota", model: "Land Cruiser" },
    { make: "Jeep", model: "Wrangler" },
    { make: "Ford", model: "F-150 Lightning" },
    { make: "Ford", model: "Bronco" },
  ]

/** Pickups / body-on-frame configs that use "{row} Schematic" when Dato sample is missing. */
export const SEAT_DIAGRAM_ROW_SCHEMATIC_MODELS: SeatDiagramVehicleModelEntry[] =
  [
    { make: "Chevrolet", model: "Silverado 1500" },
    { make: "Chevrolet", model: "Silverado 2500 HD" },
    { make: "Chevrolet", model: "Silverado 3500 HD" },
    { make: "Chevrolet", model: "Colorado" },
    { make: "GMC", model: "Sierra 1500" },
    { make: "GMC", model: "Sierra 2500 HD" },
    { make: "GMC", model: "Sierra 3500 HD" },
    { make: "GMC", model: "Canyon" },
    { make: "Ford", model: "F-150" },
    { make: "Ford", model: "F-150 Lightning" },
    { make: "Ford", model: "Bronco" },
    { make: "Toyota", model: "Tundra" },
    { make: "Toyota", model: "Tacoma" },
  ]

function normalizeVehicleToken(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ")
}

function matchesVehicleEntry(
  make: string,
  model: string,
  entries: SeatDiagramVehicleModelEntry[]
): boolean {
  const m = normalizeVehicleToken(make)
  const mod = normalizeVehicleToken(model)
  if (!m) {
    return false
  }

  return entries.some((entry) => {
    if (normalizeVehicleToken(entry.make) !== m) {
      return false
    }
    if (!entry.model?.trim()) {
      return true
    }
    return normalizeVehicleToken(entry.model) === mod
  })
}

function isSeatDiagramOption(option: FactoryMatchOption): boolean {
  return (option.title ?? "").toLowerCase().includes("seat diagram")
}

/** First legacy Dato seat-diagram `value` on the factory-match step. */
export function getDatoSeatDiagramSampleValue(
  datoSteps: Step[]
): string | null {
  const fmStep = datoSteps.find((s) => s.key === "factory-match")
  if (!fmStep) {
    return null
  }

  const matchLists = [
    ...(fmStep.factoryMatches ?? []),
    ...(fmStep.substeps?.flatMap((ss) => ss.factoryMatches ?? []) ?? []),
  ]

  for (const match of matchLists) {
    for (const option of match.factoryMatchOption ?? []) {
      if (!isSeatDiagramOption(option)) {
        continue
      }
      const value = option.value?.trim()
      if (value) {
        return value
      }
    }
  }

  return null
}

function inferModeFromDatoSample(
  sampleValue: string,
  selectedMake: string,
  selectedModel: string
): SeatDiagramDisplayMode {
  const v = sampleValue.trim().toLowerCase()
  if (/schematic/.test(v) || /\d+-\d+(-\d+)?/.test(v)) {
    return "row-schematic"
  }

  const make = normalizeVehicleToken(selectedMake)
  const model = normalizeVehicleToken(selectedModel)
  if (make && v.includes(make)) {
    if (!model || v.includes(model)) {
      return "vehicle-model"
    }
    const modelFirstWord = model.split(" ")[0]
    if (modelFirstWord.length > 2 && v.includes(modelFirstWord)) {
      return "vehicle-model"
    }
  }

  return "vehicle-model"
}

/**
 * How to label seat diagram tiles for this YMM (Dato sample first, then catalog lists).
 */
export function resolveSeatDiagramDisplayMode(
  datoSteps: Step[],
  selectedMake: string,
  selectedModel: string
): SeatDiagramDisplayMode {
  const sample = getDatoSeatDiagramSampleValue(datoSteps)
  if (sample) {
    return inferModeFromDatoSample(sample, selectedMake, selectedModel)
  }

  if (
    matchesVehicleEntry(
      selectedMake,
      selectedModel,
      SEAT_DIAGRAM_ROW_SCHEMATIC_MODELS
    )
  ) {
    return "row-schematic"
  }
  if (
    matchesVehicleEntry(
      selectedMake,
      selectedModel,
      SEAT_DIAGRAM_VEHICLE_MODEL_CATALOG
    )
  ) {
    return "vehicle-model"
  }

  return "row-schematic"
}

export function shouldUseVehicleModelForSeatDiagram(
  make: string,
  model: string
): boolean {
  return resolveSeatDiagramDisplayMode([], make, model) === "vehicle-model"
}

export function formatVehicleModelSeatDiagramLabel(
  make: string,
  model: string
): string {
  return [make, model]
    .map((p) => p.trim())
    .filter(Boolean)
    .join(" ")
}
